import { CommonModule } from '@angular/common';
import {
  Component, EventEmitter, Input, OnInit, Output
} from '@angular/core';
import {
  FormControl, FormGroup, ReactiveFormsModule, Validators
} from '@angular/forms';
import { format } from 'date-fns';

import { IAppointmentSlot } from '@sondermind/data-access/public-facing/provider-calendar';
import { IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisCardModule } from '@sondermind/iris/card';
import { IrisIconComponent } from '@sondermind/iris/icon';
import { IrisRadioGroupModule, RadioOption } from '@sondermind/iris/radio-group';
import { ILocation } from '@sondermind/utilities/models-public-facing';
import { ISessionRequestPreferences } from '@sondermind/utilities/models-scheduling';
import { SessionPreference, SessionType } from '@sondermind/utilities/models-session';

import { IProviderCalendarForm, IProviderCalendarFormInModal } from '../../models/provider-calendar-form.model';
import { AppointmentCarouselComponent, IAppointmentSlotSelection } from '../appointment-carousel/appointment-carousel.component';

import { IProviderCalendarInitSegmentData } from '../../models/segment/provider-calendar-segment-data.interface';
import { ProviderTimeZoneStringPipe } from './provider-timezone-string-pipe/provider-timezone-string.pipe';

const inPersonSessionOption: RadioOption<SessionPreference> = {
  name: 'I\'d prefer an in-person session',
  value: SessionPreference.IN_PERSON,
  iconName: 'location_on',
  iconColor: '$color-dec-kelly-100',
};

const firstSessionOptionRadioId = 'sessionTypePreference-radio-option0';
const bookSlotButtonId = 'book-calendar-slot-button';

@Component({
  selector: 'public-provider-calendar',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IrisButtonModule,
    IrisCardModule,
    IrisIconComponent,
    IrisRadioGroupModule,
    AppointmentCarouselComponent,
    ProviderTimeZoneStringPipe,
  ],
  templateUrl: './provider-calendar.component.html',
  styleUrls: ['./provider-calendar.component.scss']
})
export class ProviderCalendarComponent implements OnInit {
  @Input() appointmentSlots: IAppointmentSlot[] = [];
  @Input() headerText: string = 'Book a session';
  @Input() isInModal: boolean = false;
  @Input() providerId: number | null = null;
  @Input() providerPrimaryAddress: ILocation | null = null;
  @Input() providerSessionTypesOffered: string[] = [];
  @Input() providerTimeZone: string = '';

  @Output() requestASessionClicked: EventEmitter<ISessionRequestPreferences> =
    new EventEmitter<ISessionRequestPreferences>();

  @Output() appointmentSlotsProcessed: EventEmitter<IProviderCalendarInitSegmentData> =
    new EventEmitter<IProviderCalendarInitSegmentData>();

  @Output() bookSessionClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  browserTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
  showSessionType: boolean = false;
  sessionTypeDefaultValue: SessionPreference = SessionPreference.VIDEO;
  irisButtonTypes = IrisButtonTypes;
  addressStr = '';

  submitButtonText = 'Connect without selecting a time';
  hasSelectedTimeslot = false;

  providerCalendarFormGroup: FormGroup<IProviderCalendarForm> = new FormGroup<IProviderCalendarForm>({
    sessionTypePreference: new FormControl<SessionPreference>(this.sessionTypeDefaultValue, { nonNullable: true }),
    appointment: new FormControl<Date | null>(null)
  });

  /** We use a separate formGroup when it's open in a modal since the original formGroup is still
   * in use behind the modal.
   * Attempting to use the same formGroup for both instances leads to buggy behavior with the radio control.
   * It's also important to use different names for the form controls to avoid conflicts.
   */
  providerCalendarFormGroupInModal: FormGroup<IProviderCalendarFormInModal> =
    new FormGroup<IProviderCalendarFormInModal>({
      sessionTypePreferenceFromModal: new FormControl<SessionPreference>(
        this.sessionTypeDefaultValue, { nonNullable: true }
      ),
      appointmentFromModal: new FormControl<Date | null>(null)
    });

  sessionTypeOptions: Array<RadioOption<SessionPreference>> = [
    {
      name: 'I\'d prefer a video session',
      value: SessionPreference.VIDEO,
      iconName: 'videocam',
      iconColor: '$color-dec-kelly-100'
    },
    inPersonSessionOption,
  ];

  ngOnInit(): void {
    this.showSessionType = this.handleSessionTypeRadio();
    this.handleDefaultSessionPreference();
  }

  handleAppointmentSlotsProcessed(appointmentSlotsData: IProviderCalendarInitSegmentData): void {
    this.appointmentSlotsProcessed.emit(appointmentSlotsData);
  }

  handleAppointmentSlotSelectionChanged(newSelection: IAppointmentSlotSelection | null): void {
    this.patchAppointmentSelection(newSelection?.startTime ?? null);

    this.hasSelectedTimeslot = newSelection != null;

    if (newSelection == null) {
      this.submitButtonText = 'Connect without selecting a time';
    } else {
      // if there is a date value, the button reads "Book Mon, Jan 37 at 4:00 am"

      const formattedDateTime = format(newSelection.startTime, "EEE, MMM d 'at' h:mm a");

      this.submitButtonText = `Book ${formattedDateTime}`;
    }
  }

  private patchAppointmentSelection(newSelection: Date | null): void {
    if (this.isInModal) {
      this.providerCalendarFormGroupInModal.patchValue({ appointmentFromModal: newSelection });
    } else {
      this.providerCalendarFormGroup.patchValue({ appointment: newSelection });
    }
  }

  handleExitCalendarFocus(): void {
    let elemToFocus: HTMLElement | null;
    if (this.showSessionType) {
      elemToFocus = document.getElementById(firstSessionOptionRadioId);
    } else {
      // Get the button input nested within the iris-button
      elemToFocus = document.getElementById(bookSlotButtonId)?.children.item(0) as HTMLElement;
    }
    elemToFocus?.focus();
  }

  private handleSessionTypeRadio(): boolean {
    // we only want to show the session type radio input when:
    // 1. the provider offers BOTH in-person AND virtual
    // 2. the provider has a location that's marked default (passed in as provider primary address)
    if (this.providerSessionTypesOffered.length > 1 && this.providerPrimaryAddress) {
      // set the validator to true -- the input is now required.
      this.setSessionTypeRequiredValidator();

      // set up the input to display the address
      // eslint-disable-next-line max-len
      const cityStatePostal = `${this.providerPrimaryAddress?.city} ${this.providerPrimaryAddress?.state}, ${this.providerPrimaryAddress?.postal_code}`;

      this.addressStr = this.providerPrimaryAddress?.address_line1
        ? `${this.providerPrimaryAddress?.address_line1}, ${cityStatePostal}`
        : `${cityStatePostal}`;

      inPersonSessionOption.details = this.addressStr;

      // return that session type radio input should indeed show
      return true;
    }

    // otherwise, return that the session type radio input should NOT show
    return false;
  }

  private handleDefaultSessionPreference(): void {
    // If a provider only offers one session type, instead of both,
    // set the "default" value of the form value to that session type
    if (this.providerSessionTypesOffered.length === 1) {
      this.sessionTypeDefaultValue = this.providerSessionTypesOffered.includes(SessionType.IN_PERSON)
        ? SessionPreference.IN_PERSON
        : SessionPreference.VIDEO;
    }
    this.patchDefaultSessionType();
  }

  private patchDefaultSessionType(): void {
    if (this.isInModal) {
      this.providerCalendarFormGroupInModal.controls.sessionTypePreferenceFromModal
        .patchValue(this.sessionTypeDefaultValue);
    } else {
      this.providerCalendarFormGroup.controls.sessionTypePreference
        .patchValue(this.sessionTypeDefaultValue);
    }
  }

  private setSessionTypeRequiredValidator(): void {
    if (this.isInModal) {
      this.providerCalendarFormGroupInModal.controls.sessionTypePreferenceFromModal.setValidators(Validators.required);
    } else {
      this.providerCalendarFormGroup.controls.sessionTypePreference.setValidators(Validators.required);
    }
  }

  onSubmit(): void {
    const selectedStartTime = this.isInModal ?
      this.providerCalendarFormGroupInModal.controls.appointmentFromModal.value :
      this.providerCalendarFormGroup.controls.appointment.value;

    let sessionType: SessionPreference | undefined;
    if (this.showSessionType) {
      sessionType = this.isInModal
        ? this.providerCalendarFormGroupInModal.controls.sessionTypePreferenceFromModal.value
        : this.providerCalendarFormGroup.controls.sessionTypePreference.value;
    }

    const sessionRequest: ISessionRequestPreferences = {
      startTime: selectedStartTime,
      endTime: selectedStartTime
        ? new Date(selectedStartTime.getTime() + 60 * 60 * 1000) // One hour after startTime
        : undefined,
      sessionType,
    };

    this.requestASessionClicked.emit(sessionRequest);
  }
}
